// src/TranslationModal.tsx
// NOTE: When editing this file, update frontend/src/__tests__/TranslationModal.test.tsx
// and run `../scripts/npm-local.sh test -- TranslationModal --watch=false`.
import React from 'react'
import type { TourProps } from 'antd'
import {
  Modal,
  Button,
  Select,
  Tooltip,
  Tag,
  Alert,
  Collapse,
  Popconfirm,
  Checkbox,
  Input,
  theme,
} from 'antd'
import {
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
  DownOutlined,
} from '@ant-design/icons'
import { 
  isLanguageBeta,
  isLanguageNew,
  getQuotationStyle,
  hasQuotationOptions,
  getDefaultQuotationStyle,
  getQuotationTooltip 
} from './LanguageConfig'
import AIModelSelect from './AIModelSelect';
import { useOrderedModels } from './AIModelConfig';
import type { OrderedModel } from './AIModelConfig';
import { useSelectedAIModel } from './hooks/useSelectedAIModel';
import InsufficientCreditsAlert from './components/InsufficientCreditsAlert';
import FrenchNarrationInfo, { FrenchNarrationTooltipContent } from './components/FrenchNarrationInfo';
import RecommendedTag from './components/RecommendedTag'
import { isModelDisabledInLanguage } from './utils/aiModelService';
import type { FrenchNarrativeTone } from './types';
import type { CreditTopUpContext } from './types/creditTopUp';
import TranslationScopeSection from './translationModal/TranslationScopeSection';
import TranslationQualitySection from './translationModal/TranslationQualitySection';
import TranslationReadinessSection from './translationModal/TranslationReadinessSection';
import TranslationGlossaryReadinessNotice from './translationModal/TranslationGlossaryReadinessNotice';
import TranslationCharacterAnalysisControl from './translationModal/TranslationCharacterAnalysisControl';
import ModalCostBreakdown, { type ModalCostBreakdownRow } from './components/ModalCostBreakdown';
import {
  buildTranslationReadinessViewModel,
  resolveFrenchLanguageBehaviorSummary,
  resolveTranslationSummaryState,
} from './translationModal/translationModalViewModels';
import { resolveFrontendApiBase } from './utils/apiBase';
import AppTour, { AppTourVideoLink, renderAppTourPanel } from './tours/AppTour';
import { tourTarget, useGuidedTour } from './tours/useGuidedTour';
import { tutorialVideoLinks } from './tutorialVideoLinks';
import { DEFAULT_RECOMMENDED_TRANSLATION_MODEL_ID } from './recommendedTranslationWorkflow'

// Temporarily disable queued/batch translation until validation/proofreading compatibility is restored
const BATCH_TRANSLATION_ENABLED = false;

// Temporarily disable the entire translation modal while we fix stalled job issues
const TRANSLATION_MODAL_DISABLED = false;
const AUTO_FRENCH_TONE_VALUE = 'AI Detect'

const EASY_MODE_MODEL_ID = DEFAULT_RECOMMENDED_TRANSLATION_MODEL_ID
const EASY_MODE_ADVANCED_OPTION_VALUE = 'easyModeGemini3FlashProofread'
const PROOFREADER_SURCHARGE_PCT = 15
const MAX_MULTI_TARGET_LANGUAGES = 3
const COMPLETION_TAG_LABEL = 'Completed'
const LANGUAGE_STATUS_TAG_STYLE: React.CSSProperties = {
  marginInlineStart: 0,
  marginInlineEnd: 0,
  paddingInline: 4,
  height: 16,
  lineHeight: '14px',
  fontSize: 10,
  borderRadius: 4,
}
const TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION = 'fr_dialogue_v5_traditional'
const API_BASE = resolveFrontendApiBase();

type TermScanWarningState = 'missing' | 'running' | 'failed' | 'needs_review';
type TranslationCompletionMode = 'book' | 'chapter';

export type TranslationBetaFeedbackPayload = {
  source: 'translation_modal_beta';
  targetLanguage: string;
  issueType: 'quality' | 'style' | 'formatting' | 'terminology' | 'other';
  feedback: string;
  contactEmail?: string;
  includeContext: boolean;
  translateVersion: string;
  workflowMode: 'easy' | 'advanced';
  selectedAIModel: string;
  quotationStyle: string;
  autoProofreadEnabled: boolean;
  requireCharacterAnalysis?: boolean;
  termScanWarningState?: TermScanWarningState | null;
}

interface TranslationModalProps {
  isVisible: boolean
  onCancel: () => void
  onTranslate: () => void
  onDetectFrenchTone?: () => Promise<string>
  selectedChapter: any
  translateVersion: string
  setTranslateVersion: (version: string) => void
  bookLanguage: string
  targetLanguage: string
  setTargetLanguage: React.Dispatch<React.SetStateAction<string>>
  targetLanguages: string[]
  setTargetLanguages: React.Dispatch<React.SetStateAction<string[]>>
  handleLanguageChange: (value: string, type: 'source' | 'target') => void
  handleAIModelChange: (value: string) => void
  workflowMode: 'easy' | 'advanced'
  setWorkflowMode: (mode: 'easy' | 'advanced') => void
  preferredModelId: string
  recommendedWorkflowPolicy?: {
    name?: string
    proofreaderMode?: 'included' | 'optional'
    recommendedCopyVariant?: string
    validation_enabled?: boolean
    final_alignment_enabled?: boolean
  }
  onAdvancedModeChange?: (isAdvanced: boolean) => void
  languages: string[]
  betaLanguages: string[]
  betaLanguageWarning: boolean
  setBetaLanguageWarning: (warning: boolean) => void
  quotationStyle: string
  setQuotationStyle: (style: string) => void
  frenchDialogueRulesetVersion?: string
  setFrenchDialogueRulesetVersion?: (version: string) => void
  selectedAIModel: string
  creditBalance: any
  getCreditCost: () => number
  getCreditCostByLanguage?: (language: string) => number
  isLanguageTranslationComplete?: (
    language: string,
    options?: { mode: TranslationCompletionMode; chapterGuid?: string }
  ) => boolean
  autoProofreadEnabled: boolean
  setAutoProofreadEnabled: (value: boolean) => void
  requireCharacterAnalysis: boolean
  characterAnalysisAlreadyAvailable?: boolean
  onRequireCharacterAnalysisChange: (enabled: boolean) => void
  isChapterTranslated: boolean
  hasUntranslatedChapters: boolean
  userHasEnoughCredits: boolean
  hasUnproofreadChapters: boolean
  proofreadOptionProps: {
    value: string
    disabled: boolean
    text: string
  }
  enforceFrenchEmDash: boolean
  setEnforceFrenchEmDash: (v: boolean) => void
  isLangTesterUser: boolean
  frenchTone: string
  setFrenchTone: (value: string) => void
  useBatch: boolean
  setUseBatch: (value: boolean) => void
  modelOptions?: OrderedModel[]
  modelsLoading?: boolean
  bookFrenchTone?: FrenchNarrativeTone
  termScanWarningState?: TermScanWarningState | null
  termScanPendingReviewCount?: number | null
  termScanWarningDetail?: string | null
  onDismissTermScanWarning?: (mode: 'permanent' | 'session') => void
  onOpenGlossaryScanModal?: () => void
  onReviewGlossaryScanModal?: () => void
  isOpeningGlossaryScan?: boolean
  isCheckingGlossaryScanStatus?: boolean
  translationAutoRunTermScanBeforeBulk?: boolean
  termScanPreflightExtraCreditCost?: number
  termScanPreflightExtraCreditCostByLanguage?: Record<string, number>
  showTermScanAutoRunSetting?: boolean
  onTranslationAutoRunTermScanBeforeBulkChange?: (enabled: boolean) => void
  onTopUpCredits?: (context: CreditTopUpContext) => void
  onSubmitBetaFeedback?: (payload: TranslationBetaFeedbackPayload) => Promise<void> | void
  betaFeedbackEmail?: string
  simpleMode?: boolean
  tourUserId?: string
}

const TranslationModal: React.FC<TranslationModalProps> = ({
  isVisible,
  onCancel,
  onTranslate,
  onDetectFrenchTone,
  selectedChapter,
  translateVersion,
  setTranslateVersion,
  bookLanguage,
  targetLanguage,
  setTargetLanguage,
  targetLanguages,
  setTargetLanguages,
  handleLanguageChange,
  handleAIModelChange,
  workflowMode,
  setWorkflowMode,
  preferredModelId,
  recommendedWorkflowPolicy,
  onAdvancedModeChange,
  languages,
  betaLanguages,
  betaLanguageWarning,
  setBetaLanguageWarning,
  quotationStyle,
  setQuotationStyle,
  frenchDialogueRulesetVersion = TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION,
  setFrenchDialogueRulesetVersion,
  selectedAIModel,
  creditBalance,
  getCreditCost,
  getCreditCostByLanguage,
  isLanguageTranslationComplete,
  autoProofreadEnabled,
  setAutoProofreadEnabled,
  requireCharacterAnalysis,
  characterAnalysisAlreadyAvailable = false,
  onRequireCharacterAnalysisChange,
  isChapterTranslated,
  hasUntranslatedChapters,
  userHasEnoughCredits,
  hasUnproofreadChapters,
  proofreadOptionProps,
  enforceFrenchEmDash,
  setEnforceFrenchEmDash,
  isLangTesterUser,
  frenchTone,
  setFrenchTone,
  useBatch,
  setUseBatch,
  modelOptions,
  modelsLoading,
  bookFrenchTone,
  termScanWarningState,
  termScanPendingReviewCount,
  termScanWarningDetail,
  onDismissTermScanWarning,
  onOpenGlossaryScanModal,
  onReviewGlossaryScanModal,
  isOpeningGlossaryScan,
  isCheckingGlossaryScanStatus = false,
  translationAutoRunTermScanBeforeBulk = false,
  termScanPreflightExtraCreditCost = 0,
  termScanPreflightExtraCreditCostByLanguage,
  showTermScanAutoRunSetting = true,
  onTranslationAutoRunTermScanBeforeBulkChange,
  onTopUpCredits,
  onSubmitBetaFeedback,
  betaFeedbackEmail = '',
  simpleMode = false,
  tourUserId,
}) => {
  const { token } = theme.useToken()
  const selectedModelData = useSelectedAIModel(selectedAIModel);
  const isSingleTargetOnly = Boolean(simpleMode || selectedChapter)
  const batchFeatureEnabled = BATCH_TRANSLATION_ENABLED && !simpleMode
  const effectiveUseBatch = batchFeatureEnabled ? useBatch : false
  const isMultiTargetMode = !isSingleTargetOnly && targetLanguages.length > 1
  const hasTargetSelection = isSingleTargetOnly
    ? Boolean((targetLanguage || '').trim())
    : targetLanguages.length > 0
  const targetLanguageLabel = isMultiTargetMode
    ? `${targetLanguages.length} selected languages`
    : targetLanguage
  const originalLanguageLabel = (bookLanguage || 'Original').trim() || 'Original'
  const normalizedBookFrenchTone = React.useMemo(() => {
    const raw = (bookFrenchTone || '').trim().toLowerCase()
    if (!raw) return ''
    if (raw.startsWith('modern')) return 'modern'
    if (raw.startsWith('classical')) return 'classical'
    if (raw.startsWith('present')) return 'present'
    return raw
  }, [bookFrenchTone])
  const [detectingFrenchTone, setDetectingFrenchTone] = React.useState(false)
  const [detectedFrenchTone, setDetectedFrenchTone] = React.useState('')
  const [frenchDetectionError, setFrenchDetectionError] = React.useState('')
  const frenchSelectionRef = React.useRef(frenchTone)
  const frenchModalVisibleRef = React.useRef(isVisible)
  frenchModalVisibleRef.current = isVisible
  frenchSelectionRef.current = frenchTone
  const handleConfirmedTranslate = async () => {
    if (isFrenchTarget && (!frenchTone || frenchTone === AUTO_FRENCH_TONE_VALUE)) {
      if (detectingFrenchTone) return
      if (!onDetectFrenchTone) {
        setFrenchDetectionError('Choose a French narrative tense before translation.')
        return
      }
      setDetectingFrenchTone(true)
      setFrenchDetectionError('')
      const originalChoice = frenchTone
      try {
        const detected = await onDetectFrenchTone()
        if (frenchModalVisibleRef.current && frenchSelectionRef.current === originalChoice) {
          setDetectedFrenchTone(detected)
          setFrenchTone(detected)
        }
      } catch {
        setFrenchDetectionError('Detection failed. Choose a French narrative tense manually or try again.')
      } finally {
        setDetectingFrenchTone(false)
      }
      return // Show the recommendation; a second click confirms the concrete selection.
    }
    onTranslate()
  }
  const [fineTuneExpanded, setFineTuneExpanded] = React.useState(false)
  const [showReadinessDetails, setShowReadinessDetails] = React.useState(false)
  const [isLanguageCostBreakdownModalVisible, setIsLanguageCostBreakdownModalVisible] = React.useState(false)
  const [isBetaFeedbackModalVisible, setIsBetaFeedbackModalVisible] = React.useState(false)
  const [betaFeedbackIssueType, setBetaFeedbackIssueType] = React.useState<TranslationBetaFeedbackPayload['issueType']>('quality')
  const [betaFeedbackText, setBetaFeedbackText] = React.useState('')
  const [betaFeedbackContactEmail, setBetaFeedbackContactEmail] = React.useState(betaFeedbackEmail)
  const [betaFeedbackIncludeContext, setBetaFeedbackIncludeContext] = React.useState(true)
  const [betaFeedbackSubmitting, setBetaFeedbackSubmitting] = React.useState(false)
  const [betaFeedbackError, setBetaFeedbackError] = React.useState<string | null>(null)
  const workflowInitRef = React.useRef(false)
  const wasVisibleRef = React.useRef(false)
  const skipWorkflowSyncRef = React.useRef(false)
  const userWorkflowOverrideRef = React.useRef(false)
  const userModelOverrideRef = React.useRef(false)
  // The Recommended option and its underlying model are distinct UI choices.
  const [explicitModelEntry, setExplicitModelEntry] = React.useState(false)
  const effectiveRequireCharacterAnalysis = characterAnalysisAlreadyAvailable
    ? true
    : Boolean(requireCharacterAnalysis)
  const resolvedPreferredModelId = (preferredModelId || '').trim() || EASY_MODE_MODEL_ID

  const { orderedModels: fallbackModelOptions, loading: fallbackModelsLoading } = useOrderedModels(
    targetLanguage,
    isLangTesterUser,
    bookLanguage
  )
  const resolvedModelOptions = React.useMemo(() => {
    if (!modelOptions || modelOptions.length === 0) return fallbackModelOptions
    if (isLangTesterUser && !modelOptions.some(({ model }) => model.testingOnly)) {
      return fallbackModelOptions
    }
    return modelOptions
  }, [modelOptions, fallbackModelOptions, isLangTesterUser])
  const resolvedModelsLoading = React.useMemo(() => {
    if (!modelOptions || modelOptions.length === 0) return fallbackModelsLoading
    if (isLangTesterUser && !modelOptions.some(({ model }) => model.testingOnly)) {
      return fallbackModelsLoading
    }
    return modelsLoading ?? false
  }, [modelOptions, modelsLoading, fallbackModelsLoading, isLangTesterUser])
  const isEasyMode = !fineTuneExpanded
  // Eligibility for French em-dash enforcement toggle
  const normalize = (s: string) => (s || '').trim().toLowerCase().replace(/[_-]+/g, ' ')
  const isFrenchTarget = hasTargetSelection && (isMultiTargetMode ? targetLanguages.includes('French') : targetLanguage === 'French')
  const isFrenchEmDash = normalize(quotationStyle) === 'french em dash'

  // Clear any lingering tone when French is not the target while modal is active
  React.useEffect(() => {
    if (!isVisible) return
    if (!isFrenchTarget && frenchTone) {
      setFrenchTone('')
    } else if (isFrenchTarget && !frenchTone) {
      setFrenchTone(normalizedBookFrenchTone || AUTO_FRENCH_TONE_VALUE)
    }
  }, [isVisible, isFrenchTarget, frenchTone, normalizedBookFrenchTone, setFrenchTone])

  React.useEffect(() => {
    if (!isVisible) return
    if (!setFrenchDialogueRulesetVersion) return
    if (frenchDialogueRulesetVersion !== TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION) {
      setFrenchDialogueRulesetVersion(TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION)
    }
  }, [
    isVisible,
    frenchDialogueRulesetVersion,
    setFrenchDialogueRulesetVersion,
  ])

  React.useEffect(() => {
    if (!isVisible) {
      setShowReadinessDetails(false)
      setIsLanguageCostBreakdownModalVisible(false)
      setIsBetaFeedbackModalVisible(false)
      setBetaFeedbackText('')
      setBetaFeedbackError(null)
      setBetaFeedbackIssueType('quality')
      setBetaFeedbackContactEmail(betaFeedbackEmail)
      setBetaFeedbackIncludeContext(true)
    }
  }, [isVisible, betaFeedbackEmail])

  React.useEffect(() => {
    if (!isBetaFeedbackModalVisible) return
    setBetaFeedbackContactEmail(betaFeedbackEmail)
  }, [betaFeedbackEmail, isBetaFeedbackModalVisible])

  const closeBetaFeedbackModal = React.useCallback(() => {
    if (betaFeedbackSubmitting) return
    setIsBetaFeedbackModalVisible(false)
    setBetaFeedbackText('')
    setBetaFeedbackError(null)
    setBetaFeedbackIssueType('quality')
    setBetaFeedbackIncludeContext(true)
    setBetaFeedbackContactEmail(betaFeedbackEmail)
  }, [betaFeedbackEmail, betaFeedbackSubmitting])

  const handleSubmitBetaFeedback = React.useCallback(async () => {
    const trimmedFeedback = betaFeedbackText.trim()
    const trimmedEmail = betaFeedbackContactEmail.trim()
    if (trimmedFeedback.length < 10) {
      setBetaFeedbackError('Please share a bit more detail (at least 10 characters).')
      return
    }
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setBetaFeedbackError('Please enter a valid email address or leave it blank.')
      return
    }
    if (!onSubmitBetaFeedback) {
      setBetaFeedbackError('Feedback submission is unavailable right now. Please try again later.')
      return
    }

    setBetaFeedbackSubmitting(true)
    setBetaFeedbackError(null)
    try {
      await onSubmitBetaFeedback({
        source: 'translation_modal_beta',
        targetLanguage: targetLanguage || '',
        issueType: betaFeedbackIssueType,
        feedback: trimmedFeedback,
        contactEmail: trimmedEmail || undefined,
        includeContext: betaFeedbackIncludeContext,
        translateVersion,
        workflowMode: fineTuneExpanded ? 'advanced' : 'easy',
        selectedAIModel,
        quotationStyle,
        autoProofreadEnabled,
        requireCharacterAnalysis: effectiveRequireCharacterAnalysis,
        termScanWarningState: termScanWarningState ?? null,
      })
      closeBetaFeedbackModal()
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message || 'Unable to submit feedback right now.'
      setBetaFeedbackError(detail)
    } finally {
      setBetaFeedbackSubmitting(false)
    }
  }, [
    autoProofreadEnabled,
    betaFeedbackContactEmail,
    betaFeedbackIncludeContext,
    betaFeedbackIssueType,
    betaFeedbackText,
    closeBetaFeedbackModal,
    fineTuneExpanded,
    onSubmitBetaFeedback,
    quotationStyle,
    effectiveRequireCharacterAnalysis,
    selectedAIModel,
    targetLanguage,
    termScanWarningState,
    translateVersion,
  ])

  const getModelDiscountPercent = React.useCallback(() => {
    const modelPct = Number(selectedModelData?.discount || 0)
    return Math.max(0, modelPct)
  }, [selectedModelData?.discount])

  const getModelPremiumPercent = React.useCallback(() => {
    const premiumPct = Number(selectedModelData?.premium || 0)
    return Math.max(0, premiumPct)
  }, [selectedModelData?.premium])

  // Helper to compute total discount percent (model + queued)
  const getTotalDiscountPercent = React.useCallback(() => {
    const modelPct = getModelDiscountPercent()
    const batchPct = effectiveUseBatch ? 5 : 0
    return Math.max(0, modelPct + batchPct)
  }, [getModelDiscountPercent, effectiveUseBatch])

  const getNetModelAdjustmentPercent = React.useCallback(() => {
    const totalDiscountPct = getTotalDiscountPercent()
    const premiumPct = getModelPremiumPercent()
    return premiumPct - totalDiscountPct
  }, [getModelPremiumPercent, getTotalDiscountPercent])

  const translationBaseCreditCost = getCreditCost()
  const normalizedTermScanPreflightExtraCost = React.useMemo(() => {
    if (selectedChapter || !translationAutoRunTermScanBeforeBulk) {
      return 0
    }
    const parsed = Number(termScanPreflightExtraCreditCost || 0)
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return 0
    }
    return Math.ceil(parsed)
  }, [selectedChapter, translationAutoRunTermScanBeforeBulk, termScanPreflightExtraCreditCost])
  const formattedTermScanPreflightExtraCost = React.useMemo(() => {
    const parsed = Number(termScanPreflightExtraCreditCost || 0)
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return ''
    }
    return new Intl.NumberFormat('en-US').format(Math.ceil(parsed))
  }, [termScanPreflightExtraCreditCost])
  const adjustedBaseCost = React.useMemo(() => {
    const netPct = getNetModelAdjustmentPercent()
    if (!netPct) return translationBaseCreditCost
    return Math.max(0, Math.ceil(translationBaseCreditCost * (1 + netPct / 100)))
  }, [translationBaseCreditCost, getNetModelAdjustmentPercent])
  const effectiveTranslationCreditCost = React.useMemo(() => {
    if (autoProofreadEnabled) {
      return Math.ceil(adjustedBaseCost * (1 + PROOFREADER_SURCHARGE_PCT / 100))
    }
    return adjustedBaseCost
  }, [autoProofreadEnabled, adjustedBaseCost])
  const baseCreditCost = React.useMemo(
    () => translationBaseCreditCost + normalizedTermScanPreflightExtraCost,
    [translationBaseCreditCost, normalizedTermScanPreflightExtraCost],
  )
  const effectiveCreditCost = React.useMemo(
    () => effectiveTranslationCreditCost + normalizedTermScanPreflightExtraCost,
    [effectiveTranslationCreditCost, normalizedTermScanPreflightExtraCost],
  )
  const displayBaseCost = React.useMemo(() => {
    return effectiveCreditCost >= baseCreditCost ? effectiveCreditCost : baseCreditCost
  }, [effectiveCreditCost, baseCreditCost])
  const displayDiscountedCost = React.useMemo(() => {
    return effectiveCreditCost < baseCreditCost ? effectiveCreditCost : undefined
  }, [effectiveCreditCost, baseCreditCost])
  const multiLanguageCostBreakdown = React.useMemo(() => {
    if (selectedChapter || targetLanguages.length <= 1 || !getCreditCostByLanguage) {
      return []
    }
    return targetLanguages.map((language) => {
      const rawCost = getCreditCostByLanguage(language)
      const numericCost = typeof rawCost === 'number' && Number.isFinite(rawCost) ? rawCost : 0
      return {
        language,
        cost: Math.max(0, Math.ceil(numericCost)),
      }
    })
  }, [selectedChapter, targetLanguages, getCreditCostByLanguage])
  const multiLanguageCostTotal = React.useMemo(() => {
    return multiLanguageCostBreakdown.reduce((total, item) => total + item.cost, 0)
  }, [multiLanguageCostBreakdown])
  const multiLanguageGrandTotal = React.useMemo(() => {
    return multiLanguageCostTotal + normalizedTermScanPreflightExtraCost
  }, [multiLanguageCostTotal, normalizedTermScanPreflightExtraCost])
  const alreadyTranslatedSelectedLanguages = React.useMemo(() => {
    return multiLanguageCostBreakdown.filter(item => item.cost === 0).map(item => item.language)
  }, [multiLanguageCostBreakdown])
  const pendingSelectedLanguages = React.useMemo(() => {
    return multiLanguageCostBreakdown.filter(item => item.cost > 0).map(item => item.language)
  }, [multiLanguageCostBreakdown])
  const completedLanguageSet = React.useMemo(() => {
    const set = new Set<string>()
    const completionMode: TranslationCompletionMode = selectedChapter ? 'chapter' : 'book'
    const selectedChapterGuid = selectedChapter?.chapter_guid
    languages
      .filter((lang) => lang !== bookLanguage)
      .forEach((lang) => {
        const completedFromCallback =
          typeof isLanguageTranslationComplete === 'function'
            ? isLanguageTranslationComplete(lang, {
                mode: completionMode,
                chapterGuid: selectedChapterGuid,
              })
            : null
        const completedFromCost =
          completionMode === 'book' && getCreditCostByLanguage
            ? (() => {
                const rawCost = getCreditCostByLanguage(lang)
                const numericCost = typeof rawCost === 'number' && Number.isFinite(rawCost) ? rawCost : 0
                return Math.max(0, Math.ceil(numericCost)) === 0
              })()
            : false
        const isCompleted =
          completionMode === 'chapter'
            ? Boolean(completedFromCallback)
            : (completedFromCallback ?? completedFromCost)
        if (isCompleted) {
          set.add(lang)
        }
      })
    return set
  }, [selectedChapter, getCreditCostByLanguage, languages, bookLanguage, isLanguageTranslationComplete])
  const isAtMultiTargetLanguageLimit = !selectedChapter && targetLanguages.length >= MAX_MULTI_TARGET_LANGUAGES
  const glossaryScanAutoRunLanguageCount = React.useMemo(() => {
    if (selectedChapter) return 0
    if (isMultiTargetMode) {
      if (multiLanguageCostBreakdown.length > 0) {
        return pendingSelectedLanguages.length
      }
      return targetLanguages.length
    }
    if (!hasTargetSelection || !hasUntranslatedChapters) {
      return 0
    }
    return 1
  }, [
    selectedChapter,
    isMultiTargetMode,
    multiLanguageCostBreakdown.length,
    pendingSelectedLanguages.length,
    targetLanguages.length,
    hasTargetSelection,
    hasUntranslatedChapters,
  ])
  const formattedGlossaryScanAutoRunLanguageCount = React.useMemo(
    () => new Intl.NumberFormat('en-US').format(Math.max(0, glossaryScanAutoRunLanguageCount)),
    [glossaryScanAutoRunLanguageCount],
  )
  const glossaryScanAutoRunSummary = React.useMemo(() => {
    if (!hasTargetSelection) {
      return 'Select target language(s) to see whether a new AI Glossary Scan will run.'
    }
    if (!translationAutoRunTermScanBeforeBulk) {
      return formattedTermScanPreflightExtraCost
        ? `Auto-run is off. Translation will still use saved glossary rules; turning it on will add ${formattedTermScanPreflightExtraCost} credits for the new AI Glossary Scan.`
        : 'Auto-run is off. Translation will still use saved glossary rules; it just will not start a new AI Glossary Scan.'
    }
    if (glossaryScanAutoRunLanguageCount <= 0) {
      return 'No glossary scan will run because the selected languages are already translated.'
    }
    const costCopy = formattedTermScanPreflightExtraCost
      ? ` Adds ${formattedTermScanPreflightExtraCost} credits.`
      : ''
    return `ScribeShadow will create a glossary scan for ${formattedGlossaryScanAutoRunLanguageCount} selected ${glossaryScanAutoRunLanguageCount === 1 ? 'language' : 'languages'} before translation.${costCopy}`
  }, [
    hasTargetSelection,
    translationAutoRunTermScanBeforeBulk,
    glossaryScanAutoRunLanguageCount,
    formattedGlossaryScanAutoRunLanguageCount,
    formattedTermScanPreflightExtraCost,
  ])

  const handleTargetLanguageChange = (value: string) => {
    setTargetLanguage(value)
    setTargetLanguages(value ? [value] : [])
    handleLanguageChange(value, 'target')
    setBetaLanguageWarning(betaLanguages.includes(value))
    
    // Set default quotation style for the language
    const defaultStyle = getDefaultQuotationStyle(value)
    setQuotationStyle(defaultStyle)
    if (value !== 'French') {
      setFrenchTone('')
    } else if (!frenchTone) {
      setFrenchTone(normalizedBookFrenchTone || AUTO_FRENCH_TONE_VALUE)
    }
  }
  const handleTargetLanguagesChange = (values: string[]) => {
    const deduped = Array.from(
      new Set(
        values
          .map(value => (value || '').trim())
          .filter(value => !!value && value !== bookLanguage),
      ),
    ).slice(0, MAX_MULTI_TARGET_LANGUAGES)
    setTargetLanguages(deduped)
    const primaryTarget = deduped[0] || ''
    setTargetLanguage(primaryTarget)
    handleLanguageChange(primaryTarget, 'target')
    setBetaLanguageWarning(deduped.some(lang => betaLanguages.includes(lang)))
    const defaultStyle = getDefaultQuotationStyle(primaryTarget)
    setQuotationStyle(defaultStyle)
    if (!deduped.includes('French')) {
      setFrenchTone('')
    } else if (!frenchTone) {
      setFrenchTone(normalizedBookFrenchTone || AUTO_FRENCH_TONE_VALUE)
    }
  }
  const handleModelSelect = React.useCallback((value: string) => {
    setExplicitModelEntry(value !== EASY_MODE_ADVANCED_OPTION_VALUE)
    if (value === EASY_MODE_ADVANCED_OPTION_VALUE) {
      userWorkflowOverrideRef.current = true
      skipWorkflowSyncRef.current = true
      setWorkflowMode('advanced')
      setFineTuneExpanded(true)
      handleAIModelChange(resolvedPreferredModelId)
      return
    }
    handleAIModelChange(value)
  }, [handleAIModelChange, resolvedPreferredModelId, setWorkflowMode])
  const handleUserModelSelect = React.useCallback((value: string) => {
    userModelOverrideRef.current = true
    handleModelSelect(value)
  }, [handleModelSelect])

  const hasQuotationChoices = !isMultiTargetMode && hasQuotationOptions(targetLanguage)
  const needsQuotationStyle =
    hasQuotationChoices && !quotationStyle
  const needsFrenchTone = !simpleMode && !isMultiTargetMode && isFrenchTarget && !frenchTone
  const needsModel = !simpleMode && !(selectedAIModel || '').trim()
  React.useEffect(() => {
    if (!targetLanguage) return
    if (!hasQuotationChoices) return
    if (quotationStyle) return
    const defaultStyle = getDefaultQuotationStyle(targetLanguage)
    if (defaultStyle) {
      setQuotationStyle(defaultStyle)
    }
  }, [targetLanguage, quotationStyle, setQuotationStyle, hasQuotationChoices])
  const {
    value: proofreadOptionValue,
    text: proofreadOptionText,
  } = proofreadOptionProps
  const modelSelectValue = React.useMemo(() => {
    if (!fineTuneExpanded) return selectedAIModel
    if (!explicitModelEntry && selectedAIModel === resolvedPreferredModelId) {
      return EASY_MODE_ADVANCED_OPTION_VALUE
    }
    return selectedAIModel
  }, [
    selectedAIModel,
    resolvedPreferredModelId,
    fineTuneExpanded,
    explicitModelEntry,
  ])
  const modelSelectExtraOptions = React.useMemo(() => {
    if (!fineTuneExpanded) return []
    return [
      {
        value: EASY_MODE_ADVANCED_OPTION_VALUE,
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            Recommended setup
            <RecommendedTag style={{ position: 'relative', top: '-0.5px' }} />
          </span>
        ),
      },
    ]
  }, [fineTuneExpanded])
  React.useEffect(() => {
    if (!isVisible) return
    if (onAdvancedModeChange) {
      onAdvancedModeChange(fineTuneExpanded)
    }
  }, [fineTuneExpanded, isVisible, onAdvancedModeChange])
  React.useEffect(() => {
    if (!isVisible) {
      userModelOverrideRef.current = false
      setExplicitModelEntry(false)
      return
    }
    userModelOverrideRef.current = false
  }, [isVisible, targetLanguage])
  React.useEffect(() => {
    if (!isVisible) return
    if (simpleMode && (fineTuneExpanded || workflowMode === 'advanced')) {
      userWorkflowOverrideRef.current = true
      skipWorkflowSyncRef.current = true
      setFineTuneExpanded(false)
      setWorkflowMode('easy')
      return
    }
    if (isMultiTargetMode && (fineTuneExpanded || workflowMode === 'advanced')) {
      userWorkflowOverrideRef.current = true
      skipWorkflowSyncRef.current = true
      setFineTuneExpanded(false)
      setWorkflowMode('easy')
      return
    }
    if (skipWorkflowSyncRef.current) {
      skipWorkflowSyncRef.current = false
      return
    }
    if (userWorkflowOverrideRef.current) {
      return
    }
    if (workflowMode === 'easy' && fineTuneExpanded) {
      setFineTuneExpanded(false)
    } else if (workflowMode === 'advanced' && !fineTuneExpanded) {
      setFineTuneExpanded(true)
    }
  }, [isVisible, workflowMode, fineTuneExpanded, isMultiTargetMode, simpleMode, setWorkflowMode])
  const displayVersionLabel = React.useMemo(() => {
    if (translateVersion === proofreadOptionValue) {
      return proofreadOptionText
    }
    return translateVersion === 'Original' ? originalLanguageLabel : (translateVersion || originalLanguageLabel)
  }, [translateVersion, proofreadOptionValue, proofreadOptionText, originalLanguageLabel])
  const quotationSummary = React.useMemo(() => {
    if (!hasTargetSelection) {
      return 'Will be chosen after target selection'
    }
    if (isMultiTargetMode) {
      return 'Chosen per target language'
    }
    const config = getQuotationStyle(targetLanguage)
    const resolved = quotationStyle || getDefaultQuotationStyle(targetLanguage) || ''
    if (!config) {
      return resolved || 'Automatic'
    }
    if (config.options) {
      const option = config.options.find(opt => opt.value === resolved)
      if (option) {
        return `${option.label} (${option.marks})`
      }
      return resolved ? resolved : 'Choose a quotation style'
    }
    if (config.fixed) {
      return `${config.fixed.name} (${config.fixed.marks})`
    }
    return resolved || 'Automatic'
  }, [hasTargetSelection, isMultiTargetMode, targetLanguage, quotationStyle])
  const frenchToneSummary = React.useMemo<React.ReactNode>(() => {
    if (!isFrenchTarget) return ''
    if (!frenchTone) {
      return 'Choose a French narrative voice.'
    }
    const isUsingSavedTone = normalizedBookFrenchTone && frenchTone === normalizedBookFrenchTone
    const savedSuffix = isUsingSavedTone ? ' Saved for this book.' : ''
    const summaryMap: Record<string, React.ReactNode> = {
      [AUTO_FRENCH_TONE_VALUE]: (
        <>
          No tense confirmed yet. <strong>Detect a recommendation, then confirm it before queuing.</strong>
        </>
      ),
      modern: (
        <>
          Selected tense: <strong>Modern/Accessible — passé composé</strong>. A conversational style for past-tense narration.
        </>
      ),
      classical: (
        <>
          Selected tense: <strong>Classical/Literary — passé simple</strong>. A formal, literary style for past-tense narration.
        </>
      ),
      present: (
        <>
          Narrative voice: <strong>Present Tense</strong>.
        </>
      ),
    }
    const summaryNode = summaryMap[frenchTone] || frenchTone
    return (
      <>
        {summaryNode}
        {savedSuffix}
        {detectedFrenchTone === frenchTone ? " AI recommendation; confirm or change it before translation." : " Manual or saved selection; detection will not change it."}
      </>
    )
  }, [isMultiTargetMode, isFrenchTarget, frenchTone, normalizedBookFrenchTone, detectedFrenchTone])
  const availableModelOptions = React.useMemo(() => {
    if (!resolvedModelOptions || !resolvedModelOptions.length || !targetLanguage) return []
    return resolvedModelOptions
      .filter(({ model }) => !model.testingOnly || isLangTesterUser)
      .filter(({ model }) => !isModelDisabledInLanguage(model, targetLanguage))
  }, [resolvedModelOptions, targetLanguage, isLangTesterUser])
  const recommendedModelId = React.useMemo(() => {
    const workflowDefault = availableModelOptions.find(({ model }) =>
      (model.defaultForWorkflows || []).includes('translation')
    )
    if (workflowDefault) return workflowDefault.model.id
    if (availableModelOptions.length) return availableModelOptions[0].model.id
    return ''
  }, [availableModelOptions])
  const recommendedModelIdForProofread = resolvedPreferredModelId || recommendedModelId
  const isPreferredModelSelected = React.useMemo(() => {
    if (!recommendedModelIdForProofread) return false
    return selectedAIModel === recommendedModelIdForProofread
  }, [selectedAIModel, recommendedModelIdForProofread])
  const shouldShowAutoProofreadToggle = fineTuneExpanded && !!selectedAIModel
  const isValidatedRecommendedWorkflow = [
    'validated_optional_proofreader',
    // Accept the previous backend payload during a rolling deployment.
    'sol_validated',
  ].includes(recommendedWorkflowPolicy?.recommendedCopyVariant || '')
  const isProofreadIncluded =
    recommendedWorkflowPolicy?.proofreaderMode !== 'optional' && isPreferredModelSelected
  const autoProofreadChecked = isProofreadIncluded || autoProofreadEnabled
  const autoProofreadDisabled = isProofreadIncluded
  const autoProofreadLabel = isProofreadIncluded
    ? 'Additional Quality Pass included'
    : `Enable Additional Quality Pass (+${PROOFREADER_SURCHARGE_PCT}% credits)`
  const autoProofreadTooltip = isProofreadIncluded
    ? 'Included with the recommended setup at no extra cost.'
    : isValidatedRecommendedWorkflow
      ? 'Extensive multilingual testing found the Recommended model performed better without a separate rewriting pass. Automated validation remains included. Enabling this optional pass adds a 15% credit surcharge.'
      : 'Runs an extra pass to improve quality. Adds a 15% credit surcharge when enabled. Recommended setup already includes this at no extra cost.'
  const characterAnalysisTooltip = characterAnalysisAlreadyAvailable
    ? 'Character analysis is already completed for this book, so translation will use the existing analysis.'
    : effectiveRequireCharacterAnalysis
      ? 'When enabled, we first analyze character relationships before translation. Recommended for more consistent pronouns, formality, and character references.'
      : 'Character analysis will be skipped. This may result in lower translation quality.'
  const glossaryAutoRunTooltip = React.useMemo(() => {
    const details = glossaryScanAutoRunSummary ? ` ${glossaryScanAutoRunSummary}` : ''
    return `Controls whether ScribeShadow starts a separate AI scan of this book's untranslated text to find glossary terms before translation. Saved Always/Never Translate rules are still used either way. For full control, run and review manually in Metadata before translating.${details}`
  }, [glossaryScanAutoRunSummary])
  React.useEffect(() => {
    if ((!shouldShowAutoProofreadToggle || isProofreadIncluded) && autoProofreadEnabled) {
      setAutoProofreadEnabled(false)
    }
  }, [shouldShowAutoProofreadToggle, isProofreadIncluded, autoProofreadEnabled, setAutoProofreadEnabled])
  React.useEffect(() => {
    if (!isVisible) {
      if (wasVisibleRef.current) {
        const desiredMode = fineTuneExpanded ? 'advanced' : 'easy'
        if (workflowMode !== desiredMode) {
          setWorkflowMode(desiredMode)
        }
      }
      if (onAdvancedModeChange) {
        onAdvancedModeChange(false)
      }
      setFineTuneExpanded(false)
      workflowInitRef.current = false
      wasVisibleRef.current = false
      userWorkflowOverrideRef.current = false
      return
    }
    if (workflowInitRef.current) return
    if (workflowMode === 'advanced' && !selectedAIModel) {
      handleModelSelect(EASY_MODE_ADVANCED_OPTION_VALUE)
    }
    setFineTuneExpanded(workflowMode === 'advanced')
    workflowInitRef.current = true
    wasVisibleRef.current = true
  }, [
    isVisible,
    fineTuneExpanded,
    workflowMode,
    setWorkflowMode,
    handleModelSelect,
    selectedAIModel,
  ])
  const defaultModelId = resolvedPreferredModelId || recommendedModelId
  React.useEffect(() => {
    if (!isVisible) return
    if (workflowMode !== 'advanced') return
    if (userModelOverrideRef.current) return
    if (!selectedAIModel || selectedAIModel === recommendedModelId) {
      if (selectedAIModel !== resolvedPreferredModelId) {
        handleAIModelChange(resolvedPreferredModelId)
      }
    }
  }, [
    isVisible,
    workflowMode,
    selectedAIModel,
    recommendedModelId,
    resolvedPreferredModelId,
    handleAIModelChange,
  ])
  React.useEffect(() => {
    if (!defaultModelId) return
    if (workflowMode === 'easy') return
    if (selectedAIModel === resolvedPreferredModelId) return
    const isSelectedAvailable = availableModelOptions.some(({ model }) => model.id === selectedAIModel)
    if (!selectedAIModel || !isSelectedAvailable) {
      handleAIModelChange(defaultModelId)
    }
  }, [defaultModelId, availableModelOptions, selectedAIModel, handleAIModelChange, workflowMode, resolvedPreferredModelId])
  const defaultLanguageQuotationStyle = React.useMemo(() => {
    return getDefaultQuotationStyle(targetLanguage) || ''
  }, [targetLanguage])
  const resolvedQuotationStyleValue = React.useMemo(() => {
    if (quotationStyle) return quotationStyle
    if (defaultLanguageQuotationStyle) return defaultLanguageQuotationStyle
    return ''
  }, [quotationStyle, defaultLanguageQuotationStyle])
  const recommendedFrenchToneValue = normalizedBookFrenchTone || AUTO_FRENCH_TONE_VALUE
  const isFrenchToneRecommended = React.useMemo(() => {
    if (!isFrenchTarget) return true
    if (normalizedBookFrenchTone) {
      return frenchTone === normalizedBookFrenchTone
    }
    return frenchTone === AUTO_FRENCH_TONE_VALUE
  }, [isFrenchTarget, frenchTone, normalizedBookFrenchTone])
  const scopeSummary = React.useMemo<React.ReactNode>(() => (
    resolveTranslationSummaryState({
      hasTargetSelection,
      isMultiTargetMode,
      versionLabel: displayVersionLabel,
      targetLanguage,
      targetLanguageCount: targetLanguages.length,
    })
  ), [hasTargetSelection, isMultiTargetMode, displayVersionLabel, targetLanguage, targetLanguages.length])
  const dialogueSummaryNode = React.useMemo<React.ReactNode>(() => {
    if (!isFrenchTarget) return ''
    const config = getQuotationStyle(targetLanguage)
    if (!config) return ''
    const defaultStyle = defaultLanguageQuotationStyle
    const resolvedStyle = resolvedQuotationStyleValue || defaultStyle
    const describeStyle = (label: string, marks?: string) =>
      marks ? `${label} (${marks})` : label
    if (config.options && config.options.length > 1) {
      const recommendedOption = config.options.find(opt => opt.value === defaultStyle) || config.options[0]
      const currentOption =
        config.options.find(opt => opt.value === resolvedStyle) ||
        recommendedOption ||
        config.options[0]
      return (
        <>
          Dialogue punctuation:{' '}
          <strong>{describeStyle(currentOption.label, currentOption.marks)}</strong>.
        </>
      )
    }
    if (config.options && config.options.length === 1) {
      const only = config.options[0]
      return (
        <>
          Dialogue punctuation: <strong>{describeStyle(only.label, only.marks)}</strong>.
        </>
      )
    }
    if (config.fixed) {
      return (
        <>
          Dialogue punctuation: <strong>{describeStyle(config.fixed.name, config.fixed.marks)}</strong>.
        </>
      )
    }
    return ''
  }, [isFrenchTarget, targetLanguage, defaultLanguageQuotationStyle, resolvedQuotationStyleValue])
  const frenchDialogueRulesetSummary = React.useMemo<React.ReactNode>(() => {
    if (isMultiTargetMode) return ''
    if (!isFrenchTarget || !isFrenchEmDash) return ''
    return (
      <>
        Ruleset: <strong>Traditional</strong>.
      </>
    )
  }, [isMultiTargetMode, isFrenchTarget, isFrenchEmDash])

  /** Compact "BETA" tag used in language lists */
  const BetaTag = (): JSX.Element => (
    <Tag
      color="orange"
      style={{ marginLeft: 8, fontSize: '0.75em', padding: '0 4px', lineHeight: 1 }}
    >
      BETA
    </Tag>
  )


  /** Gather alert messages for the bottom of the modal */
  const getStatusMessages = () => {
    const messages: {
      type: 'error' | 'warning' | 'success'
      message: string
    }[] = []

    if (!hasTargetSelection) {
      messages.push({
        type: 'error',
        message: 'Please select a target language to continue.',
      })
      return messages
    }

    // Errors
    if (needsModel) {
      messages.push({
        type: 'error',
        message: 'Please select an AI model to continue.',
      })
    }
    if (needsQuotationStyle) {
      messages.push({
        type: 'error',
        message: `Please select a quotation style for ${targetLanguage} translation.`,
      })
    }
    if (needsFrenchTone) {
      messages.push({
        type: 'error',
        message: 'Please select a French narrative style before continuing.',
      })
    }
    if (!isMultiTargetMode && !hasUntranslatedChapters && !selectedChapter) {
      messages.push({
        type: 'error',
        message:
          'All chapters have been translated for the selected target language.',
      })
    }
    if (hasUnproofreadChapters && !selectedChapter && translateVersion === 'Proofread') {
      messages.push({
        type: 'error',
        message: 'Not all chapters are proofread.',
      })
    }

    // Warnings
    if (isChapterTranslated) {
      messages.push({
        type: 'warning',
        message:
          'The selected chapter is already translated and will be overwritten.',
      })
    }
    // Success
    if (!isMultiTargetMode && hasUntranslatedChapters && !selectedChapter) {
      messages.push({
        type: 'success',
        message:
          'ScribeShadow will only translate chapters that have not yet been translated.',
      })
    }

    return messages
  }

  const availableCredits = React.useMemo(() => {
    const raw = creditBalance?.available_balance ?? creditBalance?.credit_balance
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return raw
    }
    if (typeof raw === 'string') {
      const parsed = Number(raw)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
    return null
  }, [creditBalance])
  const formatCredits = React.useCallback((value: number) => {
    return new Intl.NumberFormat('en-US').format(Math.max(0, Math.ceil(value)))
  }, [])

  // Check if user has enough credits with discount
  const hasEnoughCreditsWithDiscount =
    typeof availableCredits === 'number' && availableCredits >= effectiveCreditCost
  const translationShortfallCredits = React.useMemo(() => {
    if (typeof availableCredits !== 'number') return null
    return Math.max(Math.ceil(effectiveCreditCost - availableCredits), 0)
  }, [availableCredits, effectiveCreditCost])
  const handleTopUpCredits = React.useCallback(() => {
    if (!onTopUpCredits || typeof availableCredits !== 'number') return
    const requiredCredits = Math.max(0, Math.ceil(effectiveCreditCost))
    const normalizedAvailable = Math.max(0, Math.floor(availableCredits))
    onTopUpCredits({
      source: 'translation_modal',
      requiredCredits,
      availableCredits: normalizedAvailable,
      shortfallCredits: Math.max(requiredCredits - normalizedAvailable, 0),
    })
  }, [availableCredits, effectiveCreditCost, onTopUpCredits])

  const termScanWarningLanguage = isMultiTargetMode
    ? (
      glossaryScanAutoRunLanguageCount > 0
        ? `${formattedGlossaryScanAutoRunLanguageCount} selected ${glossaryScanAutoRunLanguageCount === 1 ? 'language' : 'languages'}`
        : 'the selected languages'
    )
    : (targetLanguage || 'this language')
  const termScanAutoRunEnabledForBook = !selectedChapter && Boolean(translationAutoRunTermScanBeforeBulk)
  const termScanWarningDetailText = (termScanWarningDetail || '').toString().trim().toLowerCase()
  const termScanWarningTimedOut = termScanWarningDetailText.includes('timed out')
  const disableTranslateWhileTermScanRunning =
    !isMultiTargetMode &&
    !selectedChapter &&
    termScanAutoRunEnabledForBook &&
    termScanWarningState === 'running'
  const disableTranslateButton =
    !hasTargetSelection ||
    !translateVersion ||
    (!isMultiTargetMode && bookLanguage === targetLanguage) ||
    (!isMultiTargetMode && !hasUntranslatedChapters && !selectedChapter) ||
    !hasEnoughCreditsWithDiscount ||
    (translateVersion === 'Proofread' && hasUnproofreadChapters && !selectedChapter) ||
    needsModel ||
    needsQuotationStyle ||
    needsFrenchTone ||
    disableTranslateWhileTermScanRunning ||
    isCheckingGlossaryScanStatus
  const reviewTranslationDisabledReason = (() => {
    if (!disableTranslateButton) return ''
    if (isCheckingGlossaryScanStatus) {
      return 'Checking AI Glossary Scan status.'
    }
    if (!hasTargetSelection) {
      return 'Select a target language to continue.'
    }
    if (!translateVersion) {
      return 'Select source text to continue.'
    }
    if (!isMultiTargetMode && bookLanguage === targetLanguage) {
      return 'Choose a target language different from the source language.'
    }
    if (!isMultiTargetMode && !hasUntranslatedChapters && !selectedChapter) {
      return 'All chapters have already been translated for this target language.'
    }
    if (!hasEnoughCreditsWithDiscount) {
      return translationShortfallCredits && translationShortfallCredits > 0
        ? `Add ${formatCredits(translationShortfallCredits)} credits to continue.`
        : 'Add credits to continue.'
    }
    if (translateVersion === 'Proofread' && hasUnproofreadChapters && !selectedChapter) {
      return 'Proofread all chapters before translating from the proofread version.'
    }
    if (needsModel) {
      return 'Please select an AI model to continue.'
    }
    if (needsQuotationStyle) {
      return `Select a quotation style for ${targetLanguage} translation.`
    }
    if (needsFrenchTone) {
      return 'Select a French narrative style before continuing.'
    }
    if (disableTranslateWhileTermScanRunning) {
      return 'Wait for the AI Glossary Scan to finish before reviewing translation.'
    }
    return ''
  })()
  const termScanWarningMessage = (() => {
    if (!termScanWarningState) return ''
    if (termScanWarningState === 'missing') {
      if (termScanAutoRunEnabledForBook) {
        return `No glossary scan exists for ${termScanWarningLanguage} yet. ScribeShadow will create it before translation. Run it now only if you want to review terms first.`
      }
      return `AI Glossary Scan hasn't been run for ${termScanWarningLanguage} yet. Run it before translating to catch key terms.`
    }
    if (termScanWarningState === 'running') {
      return `AI Glossary Scan is running for ${termScanWarningLanguage}. Please wait for it to finish before translating.`
    }
    if (termScanWarningState === 'needs_review') {
      return ''
    }
    if (termScanWarningTimedOut) {
      if (termScanAutoRunEnabledForBook) {
        return `The last AI Glossary Scan before translation for ${termScanWarningLanguage} timed out while waiting for auto-apply. Auto-run is on, so it will retry before translation. If this keeps happening, run and review manually in Metadata before translating.`
      }
      return `The last AI Glossary Scan before translation for ${termScanWarningLanguage} timed out while waiting for auto-apply. Consider rerunning scan/review in Metadata before translating.`
    }
    if (termScanAutoRunEnabledForBook) {
      return `The last AI Glossary Scan for ${termScanWarningLanguage} didn't complete. Auto-run is on, so it will retry automatically before translation. If you want full control, run and review manually in Metadata before translating.`
    }
    return `The last AI Glossary Scan for ${termScanWarningLanguage} didn't complete. Consider rerunning it before translating.`
  })()
  const pendingReviewCount = React.useMemo(() => {
    if (typeof termScanPendingReviewCount !== 'number' || !Number.isFinite(termScanPendingReviewCount)) {
      return null
    }
    return Math.max(0, Math.floor(termScanPendingReviewCount))
  }, [termScanPendingReviewCount])
  const statusMessages = getStatusMessages()
  const contextualStatusMessages = statusMessages.filter(msg => msg.type !== 'error')
  const getNoticeTone = (type: 'warning' | 'success' | 'error') => {
    if (type === 'warning') return 'warning'
    if (type === 'success') return 'success'
    return 'neutral'
  }
  const termScanNeedsReviewMessage =
    pendingReviewCount == null
      ? `AI Glossary Scan completed for ${termScanWarningLanguage}, with glossary items still pending review. Translation will still run, but pending terms won't enforce always/never translate rules until reviewed.`
      : `AI Glossary Scan has ${pendingReviewCount} pending item${pendingReviewCount === 1 ? '' : 's'} for ${termScanWarningLanguage}. Those terms will still be translated, but always/never translate rules for these terms won't be enforced until reviewed.`
  type ReadinessNotice = { kind: 'context'; key: string; message: string; tone: 'neutral' | 'warning' | 'success' }
  const readinessNotices: ReadinessNotice[] = []
  contextualStatusMessages.forEach((msg, idx) => {
    readinessNotices.push({
      kind: 'context',
      key: `status-context-${idx}`,
      message: msg.message,
      tone: getNoticeTone(msg.type),
    })
  })
  const readinessVisibleLimit = 1
  const readinessHasOverflow = readinessNotices.length > readinessVisibleLimit
  const visibleReadinessNotices = showReadinessDetails
    ? readinessNotices
    : readinessNotices.slice(0, readinessVisibleLimit)
  const modelDiscountPct = getModelDiscountPercent()
  const modelPremiumPct = getModelPremiumPercent()
  const batchPct = effectiveUseBatch ? 5 : 0
  const extraAdjustmentRows = React.useMemo(() => {
    const rows: Array<{ label: string; value: string }> = []
    if (modelDiscountPct > 0) {
      rows.push({ label: 'Model discount', value: `-${modelDiscountPct}%` })
    }
    if (modelPremiumPct > 0) {
      rows.push({ label: 'Model premium', value: `+${modelPremiumPct}%` })
    }
    if (batchPct > 0) {
      rows.push({ label: 'Queued translation discount', value: `-${batchPct}%` })
    }
    if (autoProofreadEnabled) {
      rows.push({ label: 'Additional Quality Pass', value: `+${PROOFREADER_SURCHARGE_PCT}%` })
    }
    return rows
  }, [
    modelDiscountPct,
    modelPremiumPct,
    batchPct,
    autoProofreadEnabled,
  ])
  const glossaryScanTargetLanguages = React.useMemo(() => {
    if (selectedChapter || !translationAutoRunTermScanBeforeBulk) {
      return [] as string[]
    }
    const sourceLanguage = (bookLanguage || '').trim().toLowerCase()
    const candidates = (
      isMultiTargetMode
        ? (pendingSelectedLanguages.length > 0 ? pendingSelectedLanguages : targetLanguages)
        : [targetLanguage]
    )
      .map((lang) => (lang || '').trim())
      .filter((lang) => Boolean(lang) && lang.toLowerCase() !== sourceLanguage)
    return Array.from(new Set(candidates))
  }, [
    selectedChapter,
    translationAutoRunTermScanBeforeBulk,
    bookLanguage,
    isMultiTargetMode,
    pendingSelectedLanguages,
    targetLanguages,
    targetLanguage,
  ])
  const glossaryScanCostByLanguageRows = React.useMemo(() => {
    const rows: Array<{ language: string; cost: number }> = []
    if (normalizedTermScanPreflightExtraCost <= 0) {
      return rows
    }

    const selectedLanguageLookup = new Set(
      glossaryScanTargetLanguages.map((lang) => lang.toLowerCase()),
    )
    const allocatedByLanguage = new Map<string, number>()
    Object.entries(termScanPreflightExtraCreditCostByLanguage || {}).forEach(([language, rawCost]) => {
      const normalizedLanguage = (language || '').trim()
      const parsedCost = Number(rawCost)
      if (!normalizedLanguage || !Number.isFinite(parsedCost) || parsedCost <= 0) {
        return
      }
      if (
        selectedLanguageLookup.size > 0 &&
        !selectedLanguageLookup.has(normalizedLanguage.toLowerCase())
      ) {
        return
      }
      allocatedByLanguage.set(
        normalizedLanguage,
        (allocatedByLanguage.get(normalizedLanguage) || 0) + Math.ceil(parsedCost),
      )
    })

    const orderedLanguages = glossaryScanTargetLanguages.length > 0
      ? glossaryScanTargetLanguages
      : Array.from(allocatedByLanguage.keys())
    let remainingCost = Math.max(
      0,
      normalizedTermScanPreflightExtraCost -
        Array.from(allocatedByLanguage.values()).reduce((sum, value) => sum + value, 0),
    )

    if (remainingCost > 0) {
      const missingLanguages = orderedLanguages.filter((lang) => !allocatedByLanguage.has(lang))
      if (missingLanguages.length > 0) {
        const baseShare = Math.floor(remainingCost / missingLanguages.length)
        let remainder = remainingCost % missingLanguages.length
        missingLanguages.forEach((language) => {
          const share = baseShare + (remainder > 0 ? 1 : 0)
          if (remainder > 0) remainder -= 1
          if (share > 0) {
            allocatedByLanguage.set(language, (allocatedByLanguage.get(language) || 0) + share)
          }
        })
        remainingCost = 0
      } else if (orderedLanguages.length > 0) {
        const firstLanguage = orderedLanguages[0]
        allocatedByLanguage.set(
          firstLanguage,
          (allocatedByLanguage.get(firstLanguage) || 0) + remainingCost,
        )
        remainingCost = 0
      }
    }

    orderedLanguages.forEach((language) => {
      const cost = allocatedByLanguage.get(language) || 0
      if (cost > 0) {
        rows.push({ language, cost })
      }
    })

    allocatedByLanguage.forEach((cost, language) => {
      if (cost <= 0) return
      if (rows.some((row) => row.language === language)) return
      rows.push({ language, cost })
    })

    if (rows.length === 0 && remainingCost > 0) {
      rows.push({ language: '', cost: remainingCost })
    }

    return rows
  }, [
    normalizedTermScanPreflightExtraCost,
    glossaryScanTargetLanguages,
    termScanPreflightExtraCreditCostByLanguage,
  ])
  const languageCostRows = React.useMemo<ModalCostBreakdownRow[]>(() => {
    if (isMultiTargetMode) {
      return multiLanguageCostBreakdown.map((item) => ({
        key: `language-${item.language}`,
        label: item.language,
        amountCredits: item.cost,
        displayValue: `${formatCredits(item.cost)} credits`,
        explanation: item.cost > 0 ? undefined : 'Already translated',
        tone: item.cost > 0 ? 'neutral' : 'success',
      }))
    }
    if (hasTargetSelection) {
      return [
        {
          key: `language-${targetLanguage || 'selected'}`,
          label: targetLanguage || 'Selected language',
          amountCredits: translationBaseCreditCost,
          displayValue: `${formatCredits(translationBaseCreditCost)} credits`,
          tone: 'neutral',
        },
      ]
    }
    return []
  }, [
    isMultiTargetMode,
    multiLanguageCostBreakdown,
    hasTargetSelection,
    targetLanguage,
    translationBaseCreditCost,
    formatCredits,
  ])
  const detailRows = React.useMemo<ModalCostBreakdownRow[]>(() => {
    const rows: ModalCostBreakdownRow[] = []
    rows.push(...languageCostRows)
    glossaryScanCostByLanguageRows.forEach((row) => {
      rows.push({
        key: `glossary-${row.language || 'total'}`,
        label: row.language ? `AI Glossary Scan: ${row.language}` : 'AI Glossary Scan',
        amountCredits: row.cost,
        displayValue: `+${formatCredits(row.cost)} credits`,
        explanation: 'Runs before translation so glossary terms can be detected.',
        tone: 'warning',
      })
    })
    extraAdjustmentRows.forEach((row) => {
      rows.push({
        key: `adjustment-${row.label}`,
        label: row.label,
        displayValue: row.value,
        tone: row.value.startsWith('+') ? 'warning' : 'success',
      })
    })
    return rows
  }, [languageCostRows, glossaryScanCostByLanguageRows, extraAdjustmentRows, formatCredits])
  const finalEstimatedCost = displayDiscountedCost ?? displayBaseCost
  const costBreakdownTotal = finalEstimatedCost
  const glossaryReadinessViewModel = buildTranslationReadinessViewModel({
    hasTargetSelection,
    state: termScanWarningState,
    warningMessage: termScanWarningMessage,
    needsReviewMessage: termScanNeedsReviewMessage,
  })
  const resolvedGlossaryReadinessViewModel = React.useMemo(() => {
    if (termScanWarningState === 'missing' && termScanAutoRunEnabledForBook) {
      return {
        ...glossaryReadinessViewModel,
        title: 'Glossary scan will be created',
      }
    }
    return glossaryReadinessViewModel
  }, [glossaryReadinessViewModel, termScanAutoRunEnabledForBook, termScanWarningState])

  // Compute approximate $ based on current cost and plan
  // Determine if batch is eligible (Gemini 2.5/3 Pro or ChatGPT-5)
  const isBatchEligible = React.useMemo(() => {
    const provider = selectedModelData?.provider || ''
    const apiModel = (selectedModelData?.apiModel || '').toLowerCase()
    // Eligible for queued translation if Gemini 2.5/3 Pro or OpenAI GPT-5
    const isGemini =
      apiModel.includes('gemini2.5pro') ||
      apiModel.includes('gemini-2.5-pro') ||
      apiModel.includes('gemini3pro') ||
      apiModel.includes('gemini-3-pro')
    const isGPT5 = provider.toLowerCase() === 'openai' && (apiModel.includes('gpt-5') || apiModel.includes('chatgpt-5') || apiModel.includes('chatgpt5'))
    return isGemini || isGPT5
  }, [selectedModelData])

  // Reset useBatch if model changes to a non-eligible one
  React.useEffect(() => {
    if (!batchFeatureEnabled && useBatch) {
      setUseBatch(false)
      return
    }
    if (!isBatchEligible && useBatch) {
      setUseBatch(false)
    }
  }, [batchFeatureEnabled, isBatchEligible, useBatch, setUseBatch])
  const compactFieldSectionStyle: React.CSSProperties = { marginBottom: 15 }
  const compactLabelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 5,
    lineHeight: 1.35,
  }
  const compactOptionSectionStyle: React.CSSProperties = { minWidth: 0 }
  const compactOptionTitleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 3,
  }
  const compactHelperTextStyle: React.CSSProperties = {
    fontSize: 12,
    color: token.colorTextSecondary,
    marginTop: 4,
    lineHeight: 1.35,
  }
  const versionSelectSection = (
    <div style={compactFieldSectionStyle}>
      <label style={compactLabelStyle}>
        <strong>Source text:</strong>
      </label>
      <Select
        data-testid="tutorial:translation:source-version"
        value={translateVersion}
        onChange={setTranslateVersion}
        style={{ width: '100%' }}
        suffixIcon={
          <span className="ant-select-suffix">
            <DownOutlined className="modal-token-icon" />
          </span>
        }
      >
        <Select.Option value="Original">{originalLanguageLabel}</Select.Option>
        {!simpleMode && (
          <Select.Option
            value={proofreadOptionValue}
            disabled={proofreadOptionProps.disabled}
          >
            {proofreadOptionText}
          </Select.Option>
        )}
      </Select>
    </div>
  )
  const popularLanguageOrder = [
    'German', 'French', 'Italian', 'Dutch', 'Brazilian Portuguese', 'European Spanish',
  ]
  const availableTargetLanguages = languages.filter(lang => lang !== bookLanguage)
  const targetLanguageGroups = [
    {
      label: 'Most popular',
      languages: popularLanguageOrder.filter(lang => availableTargetLanguages.includes(lang)),
    },
    {
      label: 'Other languages',
      languages: availableTargetLanguages
        .filter(lang => !popularLanguageOrder.includes(lang))
        .sort((a, b) => a.localeCompare(b, 'en')),
    },
  ].filter(group => group.languages.length > 0)

  const targetLanguageControl = (
    <div style={compactFieldSectionStyle}>
      <label style={compactLabelStyle}>
        <strong>{isSingleTargetOnly ? 'Target Language:' : 'Target Language(s):'}</strong>
        {!isSingleTargetOnly && (
          <Tooltip
            title={`Pick up to ${MAX_MULTI_TARGET_LANGUAGES} target languages for this book. Languages marked ${COMPLETION_TAG_LABEL} are already fully translated and cannot be selected.`}
          >
            <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive modal-token-icon--inline-wide" />
          </Tooltip>
        )}
      </label>
      {isSingleTargetOnly ? (
        <Select
          data-testid="tutorial:translation:target-language"
          value={targetLanguage}
          onChange={handleTargetLanguageChange}
          style={{ width: '100%' }}
          popupMatchSelectWidth={false}
          dropdownStyle={{ maxHeight: '80vh' }}
          listHeight={500}
          suffixIcon={
            <span className="ant-select-suffix">
              <DownOutlined className="modal-token-icon" />
            </span>
          }
        >
          {targetLanguageGroups.map(group => (
            <Select.OptGroup key={group.label} label={group.label}>
              {group.languages.map((lang) => (
                <Select.Option key={lang} value={lang}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>{lang}</span>
                    {completedLanguageSet.has(lang) && (
                      <Tooltip
                        title={
                          selectedChapter
                            ? 'This chapter is already translated in this language. Selecting it will overwrite the translation.'
                            : 'All chapters are already translated in this book.'
                        }
                      >
                        <Tag
                          color="green"
                          style={LANGUAGE_STATUS_TAG_STYLE}
                        >
                          {COMPLETION_TAG_LABEL}
                        </Tag>
                      </Tooltip>
                    )}
                    {!completedLanguageSet.has(lang) && isLanguageNew(lang) && (
                      <Tag color="blue" style={LANGUAGE_STATUS_TAG_STYLE}>NEW</Tag>
                    )}
                    {isLanguageBeta(lang) && <BetaTag />}
                  </span>
                </Select.Option>
              ))}
            </Select.OptGroup>
          ))}
        </Select>
      ) : (
        <Select
          data-testid="tutorial:translation:target-language"
          mode="multiple"
          value={targetLanguages}
          onChange={handleTargetLanguagesChange}
          style={{ width: '100%' }}
          maxTagCount={1}
          maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
          maxTagTextLength={18}
          popupMatchSelectWidth={false}
          dropdownStyle={{ maxHeight: '80vh' }}
          listHeight={500}
          suffixIcon={
            <span className="ant-select-suffix">
              <DownOutlined className="modal-token-icon" />
            </span>
          }
          placeholder="Select one or more target languages"
        >
          {targetLanguageGroups.map(group => (
            <Select.OptGroup key={group.label} label={group.label}>
              {group.languages.map((lang) => (
                <Select.Option
                  key={lang}
                  value={lang}
                  disabled={
                    (isAtMultiTargetLanguageLimit && !targetLanguages.includes(lang)) ||
                    (completedLanguageSet.has(lang) && !targetLanguages.includes(lang))
                  }
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>{lang}</span>
                    {completedLanguageSet.has(lang) && (
                      <Tooltip
                        title={
                          selectedChapter
                            ? 'This chapter is already translated in this language. Selecting it will overwrite the translation.'
                            : 'All chapters are already translated in this book.'
                        }
                      >
                        <Tag
                          color="green"
                          style={LANGUAGE_STATUS_TAG_STYLE}
                        >
                          {COMPLETION_TAG_LABEL}
                        </Tag>
                      </Tooltip>
                    )}
                    {!completedLanguageSet.has(lang) && isLanguageNew(lang) && (
                      <Tag color="blue" style={LANGUAGE_STATUS_TAG_STYLE}>NEW</Tag>
                    )}
                    {isLanguageBeta(lang) && <BetaTag />}
                  </span>
                </Select.Option>
              ))}
            </Select.OptGroup>
          ))}
        </Select>
      )}
        {betaLanguageWarning && hasTargetSelection && (
          <div style={{ marginTop: 4, fontSize: 12, color: token.colorTextSecondary }}>
            <strong>
              {isMultiTargetMode
                ? 'One or more selected languages are currently in beta.'
                : `${targetLanguage} translation is currently in beta.`}
            </strong>
          </div>
        )}
    </div>
  )
  const validatedQualityPassExplanation = (
    <Collapse
      size="small"
      defaultActiveKey={[]}
      expandIconPosition="end"
      style={{
        marginTop: 8,
        background: token.colorInfoBg,
        borderColor: token.colorInfoBorder,
      }}
      items={[
        {
          key: 'recommended-model-upgrade',
          label: <strong>Our Recommended model upgraded.</strong>,
          children: (
            <span>
              Multilingual testing of the new model showed better average results without a separate rewriting pass. Automated validation remains included; the optional Additional Quality Pass is available below.
            </span>
          ),
        },
      ]}
    />
  )
  const modelControl = (
      <div className="translation-quality-control" data-testid="tutorial:translation:model">
        <label style={compactLabelStyle}>
          <strong>AI Model:</strong>
          {needsModel && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Required
            </Tag>
          )}
        </label>
        <AIModelSelect
          value={modelSelectValue}
          onChange={handleUserModelSelect}
          language={targetLanguage}
          sourceLanguage={bookLanguage}
          isLangTesterUser={isLangTesterUser}
          autoSelect={isVisible}
          modelOptions={resolvedModelOptions}
          modelOptionsLoading={resolvedModelsLoading}
          disabled={isEasyMode}
          extraOptions={modelSelectExtraOptions}
          respectValue
          preferredModelId={resolvedPreferredModelId}
          workflow="translation"
        />
        {isValidatedRecommendedWorkflow && isPreferredModelSelected
          ? validatedQualityPassExplanation
          : null}
        {isEasyMode && !isValidatedRecommendedWorkflow && (
          <div style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: 4 }}>
            Easy mode uses the recommended model automatically.
          </div>
        )}
      </div>
  )
  const quotationControl = (() => {
    const quotationConfig = getQuotationStyle(targetLanguage)

    if (!quotationConfig) {
      return (
        <div className="translation-locked-row translation-locked-row--info" data-testid="tutorial:translation:quotation-style">
          <span className="translation-modal-kicker">Quotation style</span>
          <strong>Will be chosen after target selection</strong>
        </div>
      )
    }

    if (quotationConfig.fixed) {
      return (
        <div className="translation-locked-row translation-locked-row--info" data-testid="tutorial:translation:quotation-style">
          <span className="translation-modal-kicker">Quotation style</span>
          <strong>{quotationConfig.fixed.name} ({quotationConfig.fixed.marks})</strong>
        </div>
      )
    }

    if (!quotationConfig.options) return null

    return (
      <div className="translation-quality-control" data-testid="tutorial:translation:quotation-style">
        <label style={compactLabelStyle}>
          <strong>Quotation Style:</strong>
          <Tooltip title={getQuotationTooltip(targetLanguage)}>
            <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive modal-token-icon--inline" />
          </Tooltip>
          {needsQuotationStyle && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Required
            </Tag>
          )}
        </label>
        <Select
          value={quotationStyle}
          onChange={setQuotationStyle}
          placeholder="Please select a quotation style"
          style={{
            width: '100%',
            ...(needsQuotationStyle ? { borderColor: '#ff4d4f' } : {}),
          }}
          status={needsQuotationStyle ? 'error' : undefined}
          suffixIcon={
            <span className="ant-select-suffix">
              <DownOutlined className="modal-token-icon" />
            </span>
          }
        >
          {quotationConfig.options.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label} ({option.marks})
              {option.isDefault && (
                <RecommendedTag style={{ position: 'relative', top: '-0.5px' }} />
              )}
            </Select.Option>
          ))}
        </Select>
      </div>
    )
  })()
  const frenchNarrativeSection = isFrenchTarget ? (
    <div className="translation-quality-control translation-french-narrative-control">
      <label style={compactLabelStyle}>
        <strong>Narrative Voice:</strong>
        <Tooltip
          styles={{
            root: { maxWidth: 520 },
            body: { maxWidth: 520 },
          }}
          title={
            FrenchNarrationTooltipContent
              ? React.createElement(FrenchNarrationTooltipContent)
              : undefined
          }
        >
          <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive modal-token-icon--inline" />
        </Tooltip>
        <FrenchNarrationInfo linkStyle={{ marginLeft: 8 }} variant="simple" />
      </label>
      <Select
        value={frenchTone || undefined}
        onChange={(value) => setFrenchTone(value || '')}
        placeholder="Select a narrative voice"
        style={{ width: '100%' }}
        optionLabelProp="label"
        allowClear
        suffixIcon={
          <span className="ant-select-suffix">
            <DownOutlined className="modal-token-icon" />
          </span>
        }
      >
        <Select.Option value={AUTO_FRENCH_TONE_VALUE} label="AI Detect">
          AI Detect (recommend voice & tense)
          {recommendedFrenchToneValue === AUTO_FRENCH_TONE_VALUE && <RecommendedTag />}
        </Select.Option>
        <Select.Option value="modern" label="Modern/Accessible — passé composé">
          Modern/Accessible — passé composé
          {recommendedFrenchToneValue === 'modern' && <RecommendedTag />}
        </Select.Option>
        <Select.Option value="classical" label="Classical/Literary — passé simple">
          Classical/Literary — passé simple
          {recommendedFrenchToneValue === 'classical' && <RecommendedTag />}
        </Select.Option>
        <Select.Option value="present" label="Present Tense">
          Present Tense (source narration mainly present)
          {recommendedFrenchToneValue === 'present' && <RecommendedTag />}
        </Select.Option>
      </Select>
    </div>
  ) : null
  const queuedTranslationSection = batchFeatureEnabled ? (
    <div style={compactOptionSectionStyle}>
      <div style={compactOptionTitleStyle}>
        <strong>Queued Translation</strong>
        <Tooltip title="Places each chapter in a lower priority queue. Queued translations can take up to 24 hours (though are often much faster) but offer a discount.">
          <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive modal-token-icon--inline" />
        </Tooltip>
      </div>
      <Tooltip
        title={
          !isBatchEligible
            ? 'Queued translation is only available for Gemini 2.5 Pro, Gemini 3 Pro, or ChatGPT-5.'
            : undefined
        }
      >
        <div style={{ marginTop: 3 }}>
          <Checkbox
            checked={Boolean(useBatch)}
            onChange={(event) => setUseBatch(Boolean(event.target.checked))}
            disabled={!isBatchEligible}
          >
            <span style={{ color: !isBatchEligible ? token.colorTextDisabled : undefined }}>
              Use queued translation for this job.
            </span>
          </Checkbox>
        </div>
      </Tooltip>
      <div style={compactHelperTextStyle}>
        Note: Queued translations offer a 5% credit discount, but may take up to 24 hours to complete.
      </div>
    </div>
  ) : null
  const autoProofreadSection = shouldShowAutoProofreadToggle ? (
    autoProofreadDisabled ? (
      <div className="translation-locked-row" data-testid="tutorial:translation:quality-pass">
        <span className="translation-modal-kicker">Additional Quality Pass</span>
        <strong>{autoProofreadLabel}</strong>
        <div className="translation-quality-helper">{autoProofreadTooltip}</div>
      </div>
    ) : (
      <div style={compactOptionSectionStyle} data-testid="tutorial:translation:quality-pass">
        <div style={compactOptionTitleStyle}>
          <strong>Additional Quality Pass</strong>
          <Tooltip title={autoProofreadTooltip}>
            <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive modal-token-icon--inline" />
          </Tooltip>
        </div>
        <div style={{ marginTop: 3 }}>
          <Checkbox
            checked={Boolean(autoProofreadChecked)}
            onChange={(event) => setAutoProofreadEnabled(Boolean(event.target.checked))}
          >
            {autoProofreadLabel}
          </Checkbox>
        </div>
      </div>
    )
  ) : null
  const characterAnalysisRequirementSection = (
    <TranslationCharacterAnalysisControl
      checked={effectiveRequireCharacterAnalysis}
      disabled={characterAnalysisAlreadyAvailable}
      tooltip={characterAnalysisTooltip}
      onChange={onRequireCharacterAnalysisChange}
      helperText={
        characterAnalysisAlreadyAvailable
          ? 'Character analysis is already completed for this book, so this translation will use it.'
          : effectiveRequireCharacterAnalysis
            ? 'Recommended for more consistent pronouns, formality, and character references.'
            : 'Skips character analysis and uses legacy translate-only behavior.'
      }
    />
  )
  const termScanAutoRunSection = !simpleMode && !selectedChapter && showTermScanAutoRunSetting && onTranslationAutoRunTermScanBeforeBulkChange ? (
    <div className="translation-glossary-auto-run-row" style={compactOptionSectionStyle} data-testid="tutorial:translation:glossary-scan">
      <div style={compactOptionTitleStyle}>
        <strong>Pre-translation glossary scan</strong>
        <Tooltip title={glossaryAutoRunTooltip}>
          <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive modal-token-icon--inline" />
        </Tooltip>
      </div>
      <div style={{ marginTop: 3 }}>
        <Checkbox
          checked={Boolean(translationAutoRunTermScanBeforeBulk)}
          onChange={(event) => onTranslationAutoRunTermScanBeforeBulkChange(Boolean(event.target.checked))}
        >
          Run a new glossary scan before translation
          {formattedTermScanPreflightExtraCost ? ` (+${formattedTermScanPreflightExtraCost} credits)` : ''}
        </Checkbox>
      </div>
      {translationAutoRunTermScanBeforeBulk && (
        <div style={compactHelperTextStyle}>
          {glossaryScanAutoRunSummary}
        </div>
      )}
    </div>
  ) : null
  const frenchLanguageBehaviorSummary = resolveFrenchLanguageBehaviorSummary({
    isFrenchTarget,
    toneSummary: frenchToneSummary,
    dialogueSummary: dialogueSummaryNode,
    rulesetSummary: frenchDialogueRulesetSummary,
  })
  const quickSetupSummary = React.useMemo(() => {
    if (!hasTargetSelection || fineTuneExpanded) return undefined

    const sourceVersionLabel = translateVersion === 'Original'
      ? 'Original'
      : displayVersionLabel || 'Original'
    const targetSummary = isMultiTargetMode
      ? `${targetLanguages.length} target languages`
      : targetLanguage
    const summaryRows: Array<{ label: string; value: React.ReactNode; helper?: React.ReactNode }> = [
      {
        label: 'Destination',
        value: `${sourceVersionLabel} to ${targetSummary}`,
      },
    ]

    if (isMultiTargetMode) {
      summaryRows.push({
        label: 'Dialogue',
        value: 'Recommended per language',
        helper: 'Punctuation and quality settings will follow each target language.',
      })
    } else {
      const config = getQuotationStyle(targetLanguage)
      const resolvedStyle = quotationStyle || getDefaultQuotationStyle(targetLanguage) || ''
      const describeStyle = (label: string, marks?: string) =>
        marks ? `${label} (${marks})` : label

      if (config?.options?.length) {
        const supportedStyleNames = config.options
          .map(option => option.label)
          .join(' or ')
        const defaultStyle = getDefaultQuotationStyle(targetLanguage)
        const recommendedOption = config.options.find(option => option.value === defaultStyle) || config.options[0]
        const selectedOption =
          config.options.find(option => option.value === resolvedStyle) ||
          recommendedOption
        const selectedStyleLabel = describeStyle(selectedOption.label, selectedOption.marks)
        const selectedIsRecommended = selectedOption.value === recommendedOption.value

        summaryRows.push({
          label: 'Dialogue',
          value: selectedIsRecommended
            ? `${selectedStyleLabel} (recommended)`
            : `${selectedStyleLabel} (selected)`,
          helper: `Available styles: ${supportedStyleNames}.`,
        })
      } else if (config?.fixed) {
        summaryRows.push({
          label: 'Dialogue',
          value: describeStyle(config.fixed.name, config.fixed.marks),
          helper: `${targetLanguage} standard punctuation.`,
        })
      } else if (quotationSummary && quotationSummary !== 'Automatic') {
        summaryRows.push({
          label: 'Dialogue',
          value: quotationSummary,
          helper: 'Selected punctuation style.',
        })
      } else {
        summaryRows.push({
          label: 'Dialogue',
          value: `Recommended for ${targetLanguage}`,
        })
      }
    }

    if (isProofreadIncluded) {
      summaryRows.push({
        label: 'Quality pass',
        value: 'Included',
        helper: 'Additional Quality Pass is part of the recommended setup.',
      })
    } else if (autoProofreadEnabled) {
      summaryRows.push({
        label: 'Quality pass',
        value: `Runs after translation (+${PROOFREADER_SURCHARGE_PCT}% credits)`,
      })
    } else {
      summaryRows.push({
        label: 'Quality pass',
        value: isValidatedRecommendedWorkflow ? 'Automated validation included' : 'Standard translation pass',
        helper: isValidatedRecommendedWorkflow
          ? 'The optional rewriting pass is available in Fine-tune.'
          : undefined,
      })
    }

    if (characterAnalysisAlreadyAvailable) {
      summaryRows.push({
        label: 'Book context',
        value: 'Character analysis ready',
        helper: 'The translation will use existing character analysis.',
      })
    } else if (effectiveRequireCharacterAnalysis) {
      summaryRows.push({
        label: 'Book context',
        value: 'Run character analysis first',
      })
    } else {
      summaryRows.push({
        label: 'Book context',
        value: 'Skip character analysis',
      })
    }

    if (frenchLanguageBehaviorSummary) {
      summaryRows.push({
        label: 'Narrative style',
        value: "Preserve the book's voice",
      })
    }

    return (
      <div className="translation-quality-quick-summary">
        {summaryRows.map(row => (
          <div className="translation-quality-quick-row" key={row.label}>
            <div className="translation-quality-quick-label">{row.label}</div>
            <div className="translation-quality-quick-copy">
              <strong>{row.value}</strong>
              {row.helper ? <span>{row.helper}</span> : null}
            </div>
          </div>
        ))}
      </div>
    )
  }, [
    hasTargetSelection,
    fineTuneExpanded,
    translateVersion,
    displayVersionLabel,
    isMultiTargetMode,
    targetLanguages.length,
    targetLanguage,
    quotationStyle,
    quotationSummary,
    isProofreadIncluded,
    autoProofreadEnabled,
    characterAnalysisAlreadyAvailable,
    effectiveRequireCharacterAnalysis,
    frenchLanguageBehaviorSummary,
  ])
  const qualitySectionDescription = hasTargetSelection
    ? 'Review style, quality, and language behavior for this translation.'
    : 'Select a target language to view recommended quality settings.'
  const advancedUnavailableText = isMultiTargetMode
    ? 'Fine-tune settings are unavailable in multi-language mode.'
    : undefined
  const handleBackToQuickSetup = () => {
    Modal.confirm({
      title: (
        <span style={{ color: token.colorText }}>
          Return to quick setup?
        </span>
      ),
      content: (
        <span style={{ color: token.colorTextSecondary }}>
          Any fine-tune changes you made will be lost.
        </span>
      ),
      icon: <ExclamationCircleOutlined style={{ color: token.colorWarning }} />,
      okText: 'Return to quick setup',
      cancelText: 'Stay in fine-tune',
      okButtonProps: { className: 'app-primary-cta' },
      styles: {
        content: { backgroundColor: token.colorBgElevated },
        header: { backgroundColor: token.colorBgElevated },
        body: { backgroundColor: token.colorBgElevated },
        footer: { backgroundColor: token.colorBgElevated },
      },
      maskStyle: { backgroundColor: token.colorBgMask },
      onOk: () => {
        userWorkflowOverrideRef.current = true
        skipWorkflowSyncRef.current = true
        setFineTuneExpanded(false)
        setWorkflowMode('easy')
      },
    })
  }
  const glossaryReadinessAction = (() => {
    if (!hasTargetSelection) {
      return undefined
    }
    if (termScanWarningState === 'needs_review') {
      return onReviewGlossaryScanModal ? (
        <Button
          type="primary"
          size="small"
          loading={Boolean(isOpeningGlossaryScan)}
          onClick={onReviewGlossaryScanModal}
        >
          Review Now
        </Button>
      ) : undefined
    }
    if (termScanWarningState === 'running') {
      return undefined
    }
    if (termScanWarningState && termScanWarningMessage) {
      if (!onDismissTermScanWarning && !onOpenGlossaryScanModal) return undefined
      return (
        <>
          {onOpenGlossaryScanModal && (
            <Button
              size="small"
              type="primary"
              loading={Boolean(isOpeningGlossaryScan)}
              onClick={onOpenGlossaryScanModal}
            >
              Run and review now
            </Button>
          )}
          {onDismissTermScanWarning && (
            <Popconfirm
              title="Dismiss this warning?"
              okText="Dismiss permanently"
              cancelText="Dismiss for now"
              onConfirm={() => onDismissTermScanWarning('permanent')}
              onCancel={() => onDismissTermScanWarning('session')}
            >
              <Button
                size="small"
                type="text"
                disabled={Boolean(isOpeningGlossaryScan)}
                style={{ color: token.colorText, fontWeight: 600, paddingInline: 4 }}
              >
                Dismiss
              </Button>
            </Popconfirm>
          )}
        </>
      )
    }
    return undefined
  })()
  const insufficientCreditsNotice = hasTargetSelection && !hasEnoughCreditsWithDiscount ? (
    <InsufficientCreditsAlert
      message="You do not have enough credits for this translation."
      shortfallCredits={translationShortfallCredits}
      onTopUpCredits={onTopUpCredits ? handleTopUpCredits : undefined}
      showIcon={false}
    />
  ) : null
  const readinessPanel = (
    <>
      <div data-testid="tutorial:translation:readiness-state">
        <TranslationGlossaryReadinessNotice
          viewModel={resolvedGlossaryReadinessViewModel}
          action={glossaryReadinessAction}
        />
      </div>
      {hasTargetSelection ? termScanAutoRunSection : null}
      {insufficientCreditsNotice}
    </>
  )
  const costPanel = null
  const footerCostSummary = (
    <div
      className="translation-modal-footer-summary"
      aria-live="polite"
      data-testid="tutorial:translation:credit-summary"
    >
      {hasTargetSelection ? (
        <>
          <span>
            <strong>{formatCredits(finalEstimatedCost)} credits</strong> required
          </span>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span>
            <strong>
              {typeof availableCredits === 'number' ? formatCredits(availableCredits) : 'Loading...'}
            </strong>{' '}
            available
          </span>
          {!hasEnoughCreditsWithDiscount ? (
            <>
              <span className="translation-modal-footer-summary__separator">·</span>
              <span className="translation-modal-footer-summary__warning">Top-up needed</span>
            </>
          ) : null}
        </>
      ) : (
        <>
          <span>Select a target language to calculate cost.</span>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span>
            <strong>
              {typeof availableCredits === 'number' ? formatCredits(availableCredits) : 'Loading...'}
            </strong>{' '}
            available
          </span>
        </>
      )}
      <Button
        type="link"
        size="small"
        className="translation-modal-footer-summary__details"
        aria-label="View cost breakdown"
        onClick={() => setIsLanguageCostBreakdownModalVisible(true)}
        data-tour-id="translate-modal-cost"
        data-testid="tutorial:translation:cost-details"
      >
        View details
      </Button>
    </div>
  )
  const readinessNoticeList = readinessNotices.length > 0 ? (
    <div>
      {visibleReadinessNotices.map((notice) => {
        return (
          <div
            key={notice.key}
            className={`translation-readiness-support-note translation-readiness-support-note--${notice.tone}`}
          >
            {notice.message}
          </div>
        )
      })}
    </div>
  ) : null
  const reviewDisabledReadinessNotice = reviewTranslationDisabledReason ? (
    <div className="translation-readiness-blocker-alert" role="status">
      <strong>Translation unavailable</strong>
      <span>{reviewTranslationDisabledReason}</span>
    </div>
  ) : null
  const modalTitle = selectedChapter ? 'Translate Chapter' : 'Translate Book'
  const translateTour = useGuidedTour({
    userId: tourUserId,
    apiBase: API_BASE,
    flag: 'translateBookModalTourSeen',
    enabled: isVisible,
    autoStart: true,
    ready: !TRANSLATION_MODAL_DISABLED,
  });
  const translateTourSteps = React.useMemo<TourProps['steps']>(() => ([
    {
      title: 'Choose the translation scope',
      description: renderAppTourPanel('Pick the source version and target language before adjusting translation settings.'),
      target: tourTarget('translate-modal-scope'),
    },
    {
      title: 'Tune quality settings',
      description: renderAppTourPanel(
        <>
          Quick setup uses the recommended model. Advanced settings expose model choice, quotation style, proofreading, and character analysis controls when they apply.
          <AppTourVideoLink href={tutorialVideoLinks.advancedTranslation}>
            Watch the advanced translation tutorial
          </AppTourVideoLink>
        </>
      ),
      target: tourTarget('translate-modal-quality', 'translate-modal-scope'),
    },
    {
      title: 'Check glossary readiness',
      description: renderAppTourPanel(
        <>
          Review scan warnings and choose whether the glossary scan should run before a full-book translation.
          <AppTourVideoLink href={tutorialVideoLinks.glossary}>
            Watch the glossary tutorial
          </AppTourVideoLink>
        </>
      ),
      target: tourTarget('translate-modal-readiness', 'translate-modal-quality'),
    },
    {
      title: 'Review credits',
      description: renderAppTourPanel('The readiness area shows estimated credit cost, add-ons, and any top-up or blocker messages before work starts.'),
      target: tourTarget('translate-modal-readiness'),
    },
    {
      title: 'Start translation',
      description: renderAppTourPanel('When every requirement is ready, start the translation from the footer. Book translations ask for one final confirmation.'),
      target: tourTarget('translate-modal-submit', 'translate-modal-readiness'),
    },
  ]), []);

  if (TRANSLATION_MODAL_DISABLED) {
    const disabledTitle = selectedChapter ? 'Translate Chapter' : 'Translate Book'
    return (
      <Modal
        title={
          <div style={{ width: '100%', textAlign: 'center' }}>
            {disabledTitle}
          </div>
        }
        visible={isVisible}
        onCancel={onCancel}
        footer={[
          <Button key="close" type="primary" onClick={onCancel}>
            Close
          </Button>,
        ]}
      >
        <Alert
          className="modal-alert-compact"
          type="warning"
          showIcon
          message="Translations temporarily unavailable"
          description="We're working on fixes for the issues that occasionally cause translation jobs to get stuck. Please check back soon."
        />
      </Modal>
    )
  }

  return (
    <>
      <Modal
        title={
          <div className="translation-modal-titlebar">
            <span className="translation-modal-titlebar__title">{modalTitle}</span>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleOutlined />}
              onClick={translateTour.start}
              data-tour-id="translate-modal-tour-trigger"
              className="translation-modal-titlebar__tour app-tour-trigger"
            >
              Take a tour
            </Button>
          </div>
        }
        visible={isVisible}
        onCancel={onCancel}
        centered
        className="translation-modal-footer-stable translation-launch-modal-shell"
        width="min(1080px, calc(100vw - 32px))"
        bodyStyle={{
          maxHeight: 'min(84vh, calc(100vh - 160px))',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '10px 14px 8px',
        }}
        footer={[
          ...(isFrenchTarget ? [<Alert key="french-tense-confirmation" type={frenchDetectionError ? "warning" : "info"} message={frenchDetectionError || frenchToneSummary} />] : []),
          <React.Fragment key="cost-summary">
            {footerCostSummary}
            {(!fineTuneExpanded || isMultiTargetMode) && frenchNarrativeSection}
          </React.Fragment>,
          <Button key="cancel" onClick={onCancel} data-testid="tutorial:translation:cancel">
            Cancel
          </Button>,
          <React.Fragment key="adjust-settings">
            {!simpleMode && !fineTuneExpanded && hasTargetSelection && !isMultiTargetMode && (
              <Button
                data-testid="tutorial:translation:adjust-settings"
                onClick={() => {
                  userWorkflowOverrideRef.current = true
                  handleModelSelect(EASY_MODE_ADVANCED_OPTION_VALUE)
                }}
              >
                Adjust settings
              </Button>
            )}
          </React.Fragment>,
          <Button
            key="translate"
            type="primary"
            className="app-primary-cta"
            onClick={handleConfirmedTranslate}
            loading={detectingFrenchTone || isCheckingGlossaryScanStatus}
            disabled={disableTranslateButton}
            data-tour-id="translate-modal-submit"
            data-testid="tutorial:translation:start"
          >
            Review Translation
          </Button>,
        ]}
      >
        <div
          data-testid="tutorial:translation:modal"
          className={`translation-modal-body translation-launch-modal-body${hasTargetSelection ? ' translation-launch-modal-body--split' : ''}${!hasTargetSelection ? ' translation-modal-body--no-target' : ''}`}
        >
          <div className="translation-launch-modal-column translation-launch-modal-column--setup">
            <div className="translation-launch-modal-section translation-launch-modal-section--scope" data-tour-id="translate-modal-scope">
              <TranslationScopeSection
                sourceLanguage={bookLanguage}
                languageControl={targetLanguageControl}
                targetHelper={!hasTargetSelection ? scopeSummary : undefined}
                versionControl={versionSelectSection}
              />
            </div>
            <div className="translation-launch-modal-section translation-launch-modal-section--readiness" data-tour-id="translate-modal-readiness" data-testid="tutorial:translation:cost-readiness">
              <TranslationReadinessSection
                blocker={reviewDisabledReadinessNotice}
                readinessPanel={readinessPanel}
                costPanel={costPanel}
                notices={readinessNoticeList}
                hasOverflow={readinessHasOverflow}
                showDetails={showReadinessDetails}
                overflowCount={readinessNotices.length - readinessVisibleLimit}
                onToggleDetails={() => setShowReadinessDetails(prev => !prev)}
              />
            </div>
          </div>
          <div className="translation-launch-modal-column translation-launch-modal-column--quality">
            <div className="translation-launch-modal-section translation-launch-modal-section--quality" data-tour-id="translate-modal-quality">
              <TranslationQualitySection
                isAdvanced={fineTuneExpanded}
                isMultiTargetMode={isMultiTargetMode}
                quickSetupSummary={quickSetupSummary}
                quickSetupNotice={
                  hasTargetSelection && isValidatedRecommendedWorkflow
                    ? validatedQualityPassExplanation
                    : undefined
                }
                description={qualitySectionDescription}
                canAdjustSettings={!simpleMode && hasTargetSelection}
                onAdjustSettings={() => {
                  userWorkflowOverrideRef.current = true
                  handleModelSelect(EASY_MODE_ADVANCED_OPTION_VALUE)
                }}
                onBackToQuickSetup={handleBackToQuickSetup}
                advancedDisabledText={advancedUnavailableText}
                modelControl={!simpleMode && hasTargetSelection ? modelControl : undefined}
                quotationControl={hasTargetSelection ? quotationControl : undefined}
                proofreadControl={!simpleMode && hasTargetSelection ? autoProofreadSection : undefined}
                characterAnalysisControl={!simpleMode && hasTargetSelection ? characterAnalysisRequirementSection : undefined}
                frenchOptions={!simpleMode ? frenchNarrativeSection : undefined}
                queuedTranslationControl={!simpleMode && hasTargetSelection ? queuedTranslationSection : undefined}
              />
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        title="Cost breakdown"
        visible={isLanguageCostBreakdownModalVisible}
        onCancel={() => setIsLanguageCostBreakdownModalVisible(false)}
        width={560}
        footer={[
          <Button key="close-language-cost-breakdown" onClick={() => setIsLanguageCostBreakdownModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <div data-testid="language-cost-breakdown-modal">
          <ModalCostBreakdown
            rows={detailRows}
            totalCredits={hasTargetSelection ? costBreakdownTotal : null}
            availableCredits={availableCredits}
            emptyMessage="Select target language(s) to view cost details."
            formatCredits={formatCredits}
            footerNote={
              hasTargetSelection && typeof displayDiscountedCost === 'number'
                ? `Standard estimate before adjustments: ${formatCredits(displayBaseCost)} credits.`
                : undefined
            }
          />
        </div>
      </Modal>
      <Modal
        title="Share Translation Beta Feedback"
        visible={isBetaFeedbackModalVisible}
        onCancel={closeBetaFeedbackModal}
        onOk={() => void handleSubmitBetaFeedback()}
        okText="Submit Feedback"
        confirmLoading={betaFeedbackSubmitting}
        cancelButtonProps={{ disabled: betaFeedbackSubmitting }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>
              <strong>Feedback Topic</strong>
            </label>
            <Select
              value={betaFeedbackIssueType}
              onChange={(value) => setBetaFeedbackIssueType(value as TranslationBetaFeedbackPayload['issueType'])}
              style={{ width: '100%' }}
            >
              <Select.Option value="quality">Translation quality</Select.Option>
              <Select.Option value="style">Voice or style</Select.Option>
              <Select.Option value="formatting">Formatting or punctuation</Select.Option>
              <Select.Option value="terminology">Terminology or glossary</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>
              <strong>What should we improve?</strong>
            </label>
            <Input.TextArea
              value={betaFeedbackText}
              onChange={(event) => setBetaFeedbackText(event.target.value)}
              rows={5}
              maxLength={2000}
              showCount
              placeholder="Tell us what you expected and what happened."
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>
              <strong>Contact Email (optional)</strong>
            </label>
            <Input
              value={betaFeedbackContactEmail}
              onChange={(event) => setBetaFeedbackContactEmail(event.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <Checkbox
            checked={betaFeedbackIncludeContext}
            onChange={(event) => setBetaFeedbackIncludeContext(Boolean(event.target.checked))}
          >
            Include current translation settings with this feedback.
          </Checkbox>
          {betaFeedbackError && (
            <Alert
              className="modal-alert-compact"
              type="error"
              showIcon={false}
              message={betaFeedbackError}
            />
          )}
          <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
            This helps us improve beta translations for {targetLanguage || 'this language'}.
          </div>
        </div>
      </Modal>
      <AppTour
        open={translateTour.open}
        steps={translateTourSteps}
        current={translateTour.current}
        onChange={translateTour.setCurrent}
        onClose={translateTour.close}
        onFinish={translateTour.close}
        zIndex={2400}
      />
    </>
  )
}

export default TranslationModal
